import { logger } from "@/common/logger";
import { assertNever, sleep } from "@/utils/helpers";
import {
  ComponentPropsWithoutRef,
  Ref,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import {
  InferOutput,
  literal,
  number,
  object,
  safeParse,
  string,
  union,
} from "valibot";

export interface MarimoIframeProps extends Omit<
  ComponentPropsWithoutRef<"iframe">,
  "children"
> {
  autoResize?: boolean;
  onStatusChanged?: ((status: MarimoIframeStatus) => void) | undefined;
  onOpenNotebook?: ((path: string) => void) | undefined;
}

export interface MarimoIframeHandle {
  readonly status: MarimoIframeStatus | null;
  readonly isReady: boolean;
  click(
    objectId: string,
    options?:
      | { value?: string | undefined; testid?: string | undefined }
      | undefined,
  ): void;
  change(objectId: string, options: { value: string }): void;
}

export const MarimoIframe = forwardRef(function MarimoIframe(
  {
    autoResize,
    onStatusChanged,
    onOpenNotebook,
    sandbox = "allow-scripts allow-same-origin allow-popups allow-forms allow-downloads allow-popups-to-escape-sandbox",
    allow = "fullscreen; clipboard-write",
    ...iframeProps
  }: MarimoIframeProps,
  ref: Ref<MarimoIframeHandle>,
) {
  const iframeOrigin = iframeProps.src
    ? new URL(iframeProps.src).origin
    : undefined;
  const currentStatusRef = useRef<MarimoIframeStatus | null>(
    autoResize ? null : "ready",
  );

  const onStatusChangedRef = useRef(onStatusChanged);
  onStatusChangedRef.current = onStatusChanged;

  const onOpenNotebookRef = useRef(onOpenNotebook);
  onOpenNotebookRef.current = onOpenNotebook;

  const iframeRef = useRef<HTMLIFrameElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      get status() {
        return currentStatusRef.current;
      },
      get isReady() {
        return currentStatusRef.current === "ready";
      },
      click(objectId, { value, testid } = {}) {
        if (!iframeOrigin) return;
        iframeRef.current?.contentWindow?.postMessage(
          { message: "click", objectId, value, testid },
          iframeOrigin,
        );
      },
      change(objectId, { value }) {
        if (!iframeOrigin) return;
        iframeRef.current?.contentWindow?.postMessage(
          { message: "change", objectId, value },
          iframeOrigin,
        );
      },
    }),
    [iframeOrigin],
  );

  useEffect(() => {
    if (!autoResize || !iframeOrigin) return;

    const iframe = iframeRef.current;
    if (!iframe) return;

    let handshake = false;

    const onWindowMessage = (event: MessageEvent<unknown>) => {
      if (event.origin !== iframeOrigin) return;

      const data = safeParse(IframeMessage, event.data);
      if (!data.success) {
        logger.warn(
          `Ignored message from iframe: ${JSON.stringify(data.issues)}`,
        );
        return;
      }

      if (typeof data.output === "object") {
        switch (data.output.message) {
          case "hello":
            if (handshake) {
              cleanupEffect();
            } else {
              handshake = true;
              iframe.style.height =
                iframe.height = `${roundHeight(data.output.height)}px`;
            }
            break;

          case "resize":
            if (handshake) {
              iframe.style.height =
                iframe.height = `${roundHeight(data.output.height)}px`;
            } else {
              cleanupEffect();
            }
            break;

          case "open-notebook":
            // Handled by its own listener; not part of the resize handshake.
            break;

          default:
            assertNever(data.output);
        }
      } else {
        switch (data.output) {
          case "connecting":
          case "running":
          case "ready":
            currentStatusRef.current = data.output;
            onStatusChangedRef.current?.(data.output);
            break;
          default:
            assertNever(data.output);
        }
      }
    };

    const abort = new AbortController();
    window.addEventListener("message", onWindowMessage);
    const cleanupEffect = () => {
      window.removeEventListener("message", onWindowMessage);
      abort.abort();
    };

    void (async () => {
      const millis = 1_000;
      await sleep(millis, abort.signal);
      while (!handshake) {
        iframe.contentWindow?.postMessage("hello", iframeOrigin);
        await sleep(millis, abort.signal);
      }
    })().catch((error) => {
      if (!abort.signal.aborted) {
        logger.error(`Iframe handshake error: ${String(error)}`);
      }
    });

    return cleanupEffect;
  }, [autoResize, iframeOrigin]);

  useEffect(() => {
    if (!iframeOrigin) return;

    const onWindowMessage = (event: MessageEvent<unknown>) => {
      if (event.origin !== iframeOrigin) return;

      const data = safeParse(OpenNotebookMessage, event.data);
      if (data.success) {
        onOpenNotebookRef.current?.(data.output.path);
      }
    };

    window.addEventListener("message", onWindowMessage);
    return () => window.removeEventListener("message", onWindowMessage);
  }, [iframeOrigin]);

  useEffect(() => {
    const status = currentStatusRef.current;
    if (status) {
      onStatusChanged?.(status);
    }
  }, [onStatusChanged]);

  return (
    <iframe ref={iframeRef} sandbox={sandbox} allow={allow} {...iframeProps} />
  );
});

function roundHeight(height: number): number {
  return Math.ceil(height) + 30;
}

const HelloMessage = object({
  message: literal("hello"),
  height: number(),
});

const ResizeMessage = object({
  message: literal("resize"),
  height: number(),
});

const OpenNotebookMessage = object({
  message: literal("open-notebook"),
  path: string(),
});

const ReadinessMessage = union([
  literal("connecting"),
  literal("running"),
  literal("ready"),
]);

export type MarimoIframeStatus = InferOutput<typeof ReadinessMessage>;

const IframeMessage = union([
  HelloMessage,
  ResizeMessage,
  OpenNotebookMessage,
  ReadinessMessage,
]);
type IframeMessage = InferOutput<typeof IframeMessage>;
